import React, { FC } from 'react';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Add, ZoomIn } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  CardActionArea,
  CardContent,
  CardHeader,
  CardMedia,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButtonGroupProps,
} from '@mui/material';
import {
  ToggleButtonGroup,
  ToggleButton,
  Switch as MuiSwitch,
} from '@mui/material';
import ImagePlaceholder from '../../Assets/imagePlaceholder.svg';
import { useDB } from '../../Hooks/useDB';
import { useDeleteListItem } from '../../Hooks/useDeleteListItem';
import { useNavigation } from '../../Hooks/useRoute';
import { useUIStore } from '../../Hooks/useUIStore';
import { addCategory, updateItem, updateListItem } from '../../Services/db';
import { inputFileToArrayBuffer, showFileDialog } from '../../Services/file';
import { proxy } from '../../Services/proxy';
import { getImageUrl, remove, upload } from '../../Services/storage';
import { useGlobalStyles } from '../../Styles/common';
import { ListItemView } from '../../Types/entities';
import { timeAgoIntl } from '../../Utils/datetime';
import { UNCATEGORIZED } from '../../consts';
import { Menu } from '../Menu/Menu';
import { Tooltip } from '../TouchTooltip/TouchTooltip';
import { GoogleSearch } from './GoogleSearch';
import { useStyles } from './ItemDetails.style';

type ItemDetailsProps = {
  listItem: ListItemView;
};

export const ItemDetails: FC<ItemDetailsProps> = ({ listItem }) => {
  const classes = useStyles();
  const deleteListItem = useDeleteListItem();
  const { navigateToHome, navigateTo } = useNavigation();
  const { path, url } = useRouteMatch();
  const { flexGrow, flex } = useGlobalStyles();
  const { categories } = useDB();
  const { dispatch } = useUIStore();
  const [isUploading, setIsUploading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const imageSrc = listItem?.item.image
    ? getImageUrl(listItem.item.image)
    : ImagePlaceholder;

  const handleClose = () => setOpen(false);

  const category = categories.find(
    ({ id }) => id === listItem?.item.category?.id
  );

  function onChange(
    e:
      | React.ChangeEvent<{ name?: string; value: any }>
      | SelectChangeEvent<any>
      | React.MouseEvent<HTMLElement>
  ) {
    let value = null;
    let name = null;
    let type = null;

    ({ value, name, type } = (e.currentTarget as HTMLInputElement) || {});
    if (!name) {
      ({ value, name, type } = e.target as HTMLInputElement);
    }
    if (type === 'checkbox') {
      value = (e.target as HTMLInputElement).checked;
    }
    if (name?.startsWith('item_')) {
      const [, prop] = name.split('_');
      value = value === UNCATEGORIZED ? null : value;
      updateItem(listItem!.item, {
        [prop]: value,
      });
    } else {
      updateListItem(listItem!, {
        [name!]: value,
      });
    }
  }

  async function onAddCategory(formData: FormData) {
    const name = formData.get('category') as string;
    const category = await addCategory({
      name,
    });
    if (!category?.key) {
      return;
    }
    updateItem(listItem!.item, {
      categoryId: category.key,
    });
  }

  function showAddCategory() {
    dispatch({
      type: 'DIALOG',
      payload: {
        title: 'Add Category',
        actionText: 'Add',
        content: (
          <TextField name='category' type='text' required variant='standard' />
        ),
        onAction: onAddCategory,
      },
    });
  }

  async function updateImage(uploadedPath: string) {
    try {
      if (listItem?.item.image) {
        remove(listItem.item.image);
      }
      updateItem(listItem!.item, {
        image: uploadedPath,
      });
    } catch (error) {
      console.error(`can't replace image`, error);
    }
  }

  async function onReplaceGoogle() {
    navigateTo(`${url}/google-search`);
  }

  async function fetchGoogleImage(imagePath: string, thumbnailPath?: string) {
    try {
      return await proxy(imagePath);
    } catch (error) {
      if (!thumbnailPath) {
        throw error;
      }
      // the full size image is often blocked by its host, fallback to the thumbnail
      return proxy(thumbnailPath);
    }
  }

  async function onGoogleResult(imagePath: string, thumbnailPath?: string) {
    try {
      const { arrayBuffer, contentType } = await fetchGoogleImage(
        imagePath,
        thumbnailPath
      );
      const extension = contentType.split('/')[1]?.split(/[;+]/)[0] || 'png';
      const name = `${listItem?.item.id}#${Date.now()}.${extension}`;
      const uploadedPath = await upload(name, arrayBuffer, contentType);

      await updateImage(uploadedPath);
      navigateTo(url);
    } catch (error) {
      console.error(`can't use google image`, error);
      dispatch({
        type: 'SNACK',
        payload: {
          message: "Couldn't use this image, try another one",
        },
      });
      throw error;
    }
  }

  async function onTakePhoto() {
    await onReplace('environment');
  }

  async function onReplace(capture?: 'environment' | 'user') {
    const [imageFile] = await showFileDialog(capture);
    if (!imageFile) {
      return;
    }

    try {
      setIsUploading(true);
      const file = await inputFileToArrayBuffer(imageFile);
      const name = `${listItem?.item.id}#${imageFile.name}`;
      const uploadedPath = await upload(name, file);

      await updateImage(uploadedPath);
    } finally {
      setIsUploading(false);
    }
  }

  function removeImage() {
    if (!listItem?.item.image) {
      return;
    }
    remove(listItem.item.image);
    updateItem(listItem!.item, {
      image: null,
    });
  }

  function onDeleteItem() {
    navigateToHome();
    deleteListItem(listItem);
  }

  function getTooltipContent(listItem: ListItemView) {
    return (
      <Box className={classes.tooltip}>{`${
        listItem.addedBy?.displayName ?? 'Anonymous'
      }${listItem.addedAt ? `\n${timeAgoIntl(listItem.addedAt)}` : ''}`}</Box>
    );
  }

  return (
    <div className={classes.root}>
      <Switch>
        <Route path={path} exact>
          {listItem ? (
            <>
              <CardHeader
                classes={{
                  root: classes.cardHeader,
                  content: classes.cardHeaderContent,
                }}
                title={
                  <CardActionArea
                    className={classes.cardActionArea}
                    disabled={!listItem.item.image}
                    onClick={() => listItem.item.image && setOpen(true)}
                  >
                    <CardMedia
                      className={classes.cardMedia}
                      image={imageSrc}
                      title={listItem.item.name}
                    />
                    {listItem.item.image && (
                      <Box className={classes.cardHeaderZoomIconWrapper}>
                        <ZoomIn />
                      </Box>
                    )}
                  </CardActionArea>
                }
                action={
                  isUploading ? (
                    <CircularProgress
                      className={`${classes.imageMenu} ${classes.uploadingSpinner}`}
                      color='secondary'
                    />
                  ) : (
                    <Menu className={classes.imageMenu}>
                      <MenuItem onClick={() => onReplace()}>Replace</MenuItem>
                      <MenuItem onClick={onTakePhoto}>Take photo</MenuItem>
                      <MenuItem onClick={onReplaceGoogle}>
                        Replace (Google)
                      </MenuItem>
                      {listItem.item.image && (
                        <MenuItem onClick={removeImage}>Remove</MenuItem>
                      )}
                    </Menu>
                  )
                }
              />
              <CardContent classes={{ root: classes.form }}>
                <FormControl classes={{ root: classes.formControl }}>
                  <TextField
                    name='item_name'
                    classes={{ root: classes.name }}
                    defaultValue={listItem?.item.name}
                    size='medium'
                    onChange={onChange}
                    variant='standard'
                  />
                </FormControl>
                <Grid
                  gap={1}
                  container
                  wrap='nowrap'
                  alignItems='center'
                  classes={{ root: classes.formControl }}
                >
                  <Grid item classes={{ root: flexGrow }}>
                    <FormControl classes={{ root: flex }} variant='standard'>
                      <FormLabel
                        classes={{ filled: classes.label }}
                        htmlFor='category'
                      >
                        Caetgory
                      </FormLabel>
                      <Select
                        name='item_categoryId'
                        id='category'
                        onChange={onChange}
                        value={category?.id || UNCATEGORIZED}
                        className={classes.select}
                      >
                        <MenuItem key={UNCATEGORIZED} value={UNCATEGORIZED}>
                          Uncategorized
                        </MenuItem>
                        {categories.map(({ id, name }) => (
                          <MenuItem key={id} value={id}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <IconButton
                      onClick={showAddCategory}
                      className={classes.addCategoryButton}
                      size='large'
                    >
                      <Add />
                    </IconButton>
                  </Grid>
                </Grid>
                <FormControl classes={{ root: classes.formControl }}>
                  <FormLabel htmlFor='quantity'>Quantity</FormLabel>
                  <TextField
                    name='quantity'
                    type='number'
                    defaultValue={listItem.quantity || 1}
                    onFocus={(e) => e.target.select()}
                    onChange={onChange}
                    variant='standard'
                  />
                </FormControl>
                <FormControl classes={{ root: classes.formControl }}>
                  <FormLabel htmlFor='urgency'>Urgency</FormLabel>
                  <Urgency
                    classes={{
                      root: classes.urgency,
                      grouped: classes.urgencyButton,
                    }}
                    exclusive={true}
                    onChange={onChange}
                    value={listItem.urgency || '1'}
                  />
                </FormControl>
                <FormControl classes={{ root: classes.formControl }}>
                  <FormLabel htmlFor='notes'>Note</FormLabel>
                  <TextField
                    name='note'
                    type='text'
                    multiline
                    rows={4}
                    placeholder='The blue one..'
                    defaultValue={listItem.note}
                    onChange={onChange}
                    variant='standard'
                  />
                </FormControl>
                <FormControl classes={{ root: classes.formControl }}>
                  <FormControlLabel
                    control={
                      <MuiSwitch
                        checked={!!listItem.item.requiredQuantity}
                        name='item_requiredQuantity'
                        onChange={onChange}
                      />
                    }
                    label='Required Quantity'
                  />
                </FormControl>
                {listItem.addedBy && (
                  <FormControl classes={{ root: classes.formControl }}>
                    <Tooltip title={getTooltipContent(listItem)}>
                      <Avatar
                        src={listItem.addedBy.photoURL!}
                        alt={listItem.addedBy.displayName ?? 'Anonymous'}
                        classes={{ root: classes.addedBy }}
                      />
                    </Tooltip>
                  </FormControl>
                )}
                <FormControl
                  classes={{
                    root: `${classes.formControl} ${classes.deleteItemFormControl}`,
                  }}
                >
                  <Button
                    variant='outlined'
                    color='error'
                    onClick={onDeleteItem}
                  >
                    Delete
                  </Button>
                </FormControl>
              </CardContent>
              <Modal
                classes={{ root: classes.modal }}
                open={open}
                onClose={handleClose}
              >
                {/* https://stackoverflow.com/a/73130768/863110 */}
                <>
                  <TransformWrapper disablePadding>
                    <TransformComponent>
                      <img src={imageSrc} alt={listItem.item.name} />
                    </TransformComponent>
                  </TransformWrapper>
                </>
              </Modal>
            </>
          ) : null}
        </Route>
        <Route path={`${path}/google-search`}>
          <GoogleSearch
            onSelect={onGoogleResult}
            initialQuery={listItem?.item.name}
          />
        </Route>
      </Switch>
    </div>
  );
};

const Urgency: FC<ToggleButtonGroupProps> = (props) => (
  <ToggleButtonGroup {...props}>
    {['🟢', '🟡', '🔴'].map((level, index) => (
      <ToggleButton
        key={index}
        name='urgency'
        value={`${index + 1}`}
        selected={props.value === `${index + 1}`}
      >
        {level}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);
